import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { SeoPageShell } from './SeoPageShell';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Check, X, Minus, ArrowRight, Zap, Waves, Shield, ChevronRight, Sparkles } from 'lucide-react';
import { getComparison } from './comparisons';
import { LandingProfitCalculator } from '@/components/LandingProfitCalculator';
import { SLUG_TO_COMPETITOR_ID, COMPETITOR_FEES } from '@/config/competitorFeeData';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';

/**
 * Universal, config-driven competitor comparison page.
 *
 * Phase 0b3 — GEO/LLM push. Each competitor lives in
 * `/pages/seo/comparisons.js`; this component renders a fully
 * schema-tagged, mobile-first, Answer-First comparison page from that
 * config.
 *
 * The intent is that adding a new competitor (e.g., AquaSafe, Perch,
 * ProClass) is a config edit — no component work required.
 *
 * SEO surfaces produced per page:
 *   • <title>, <meta description>, <link rel=canonical>
 *   • OG + Twitter tags
 *   • WebPage + BreadcrumbList JSON-LD (via SeoPageShell)
 *   • FAQPage JSON-LD ("Is SplashPad faster than X?" and "How to migrate")
 *   • SoftwareApplication JSON-LD with Price / OperatingSystem /
 *     ApplicationCategory / featureList / AggregateOffer
 *   • Answer-First H1 (question) + <=50 word bold answer block
 *   • High-contrast primary CTA (pool-deck outdoor readability)
 *   • Feature comparison table with sticky first column on mobile
 */

const SITE = 'https://splashpad.pro';

const Cell = ({ value }) => {
    if (value === true) return <Check className="inline h-5 w-5 text-[#10b981]" aria-label="yes" />;
    if (value === false) return <X className="inline h-5 w-5 text-[#ef4444]" aria-label="no" />;
    return <Minus className="inline h-5 w-5 text-[#94a3b8]" aria-label="partial" />;
};

export const ComparisonPage = ({ slug: propSlug }) => {
    const params = useParams();
    const slug = propSlug || params.slug;
    const cfg = getComparison(slug);

    if (!cfg) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#f4f7fb] p-6 text-center">
                <div>
                    <h1 className="font-display text-2xl font-bold text-[#0a1628]">Comparison not found</h1>
                    <p className="mt-2 text-[#1a3a5c]">We don’t have a comparison page for &ldquo;{slug}&rdquo; yet.</p>
                    <Link to="/" className="mt-4 inline-block text-[#00d4ff] underline" data-testid="comparison-not-found-home">Back to SplashPad home</Link>
                </div>
            </div>
        );
    }

    const canonical = `${SITE}/vs/${cfg.slug}`;

    const webPageJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: `${cfg.competitorName} vs. SplashPad — 2026 Comparison`,
        url: canonical,
        description: cfg.answerFirst,
        inLanguage: 'en-US',
        isPartOf: {
            '@type': 'WebSite',
            name: 'SplashPad',
            url: SITE,
        },
    };

    const breadcrumbJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'SplashPad', item: SITE + '/' },
            { '@type': 'ListItem', position: 2, name: 'Compare', item: `${SITE}/vs/${cfg.slug}` },
            { '@type': 'ListItem', position: 3, name: `vs. ${cfg.competitorName}`, item: canonical },
        ],
    };

    // Page-specific FAQPage schema for the two Answer-First FAQs. The
    // site-wide FAQPage in index.html covers broader positioning; this
    // one is optimized for the migration + speed queries LLMs love.
    const faqJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
            {
                '@type': 'Question',
                name: `Is SplashPad a ${cfg.competitorName} alternative?`,
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: `Yes. SplashPad is a ${cfg.competitorName} alternative purpose-built for independent swim instructors and small multi-coach swim schools. It replaces registration, waivers, payments, credits, and staff scheduling with a single platform designed by a working swim instructor inside the Superhero Swim University community.`,
                },
            },
            {
                '@type': 'Question',
                name: cfg.fasterQuestion,
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: cfg.fasterAnswer,
                },
            },
            {
                '@type': 'Question',
                name: cfg.migrationQuestion,
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: cfg.migrationAnswer,
                },
            },
            {
                '@type': 'Question',
                name: `Who is SplashPad better for than ${cfg.competitorName}?`,
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: `${cfg.whoIsForUs} ${cfg.competitorName} remains a good fit for ${cfg.whoIsForThem.toLowerCase()}.`,
                },
            },
            {
                '@type': 'Question',
                name: `How does SplashPad’s pricing compare to ${cfg.competitorName}?`,
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'SplashPad Starter is $39.99/mo (FREE for verified Superhero Swim University Skool community members) and SplashPad Pro is $79.99/mo with unlimited students. No per-family or per-enrollment fees. Includes a free 30-day trial.',
                },
            },
        ],
    };

    // Deep SoftwareApplication schema per competitor comparison, so LLMs
    // that scrape this page get the full product graph without
    // depending on the site-wide index.html schema.
    const softwareJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'SplashPad',
        alternateName: ['Splash Pad', 'SplashPad by SSU'],
        applicationCategory: 'BusinessApplication',
        applicationSubCategory: 'Swim Lesson Registration Software',
        operatingSystem: 'Web',
        url: SITE,
        description: `SplashPad is the swim lesson registration software designed by a swim instructor, for swim instructors. Presented here as an alternative to ${cfg.competitorName}.`,
        featureList: cfg.features.filter((f) => f.splashpad === true).map((f) => f.feature),
        offers: {
            '@type': 'AggregateOffer',
            priceCurrency: 'USD',
            lowPrice: '0',
            highPrice: '79.99',
            offerCount: '3',
            offers: [
                { '@type': 'Offer', name: 'Free 30-day trial', price: '0', priceCurrency: 'USD', availability: 'https://schema.org/InStock' },
                { '@type': 'Offer', name: 'Starter', price: '39.99', priceCurrency: 'USD', availability: 'https://schema.org/InStock', description: 'FREE for verified Superhero Swim University Skool community members.' },
                { '@type': 'Offer', name: 'Pro', price: '79.99', priceCurrency: 'USD', availability: 'https://schema.org/InStock' },
            ],
        },
        aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: '4.9',
            ratingCount: '32',
            bestRating: '5',
            worstRating: '1',
        },
    };

    // Phase 92 (site audit D5.1) — meta descriptions must stay under ~155
    // chars or Google truncates them mid-sentence. The full answer-first
    // paragraph still renders in the page body; only the <meta> is clipped
    // at a word boundary.
    const metaDescription = cfg.answerFirst.length <= 155
        ? cfg.answerFirst
        : `${cfg.answerFirst.slice(0, 152).replace(/\s+\S*$/, '')}…`;

    return (
        <SeoPageShell
            testId={`seo-page-vs-${cfg.slug}`}
            title={`${cfg.competitorName} vs. SplashPad — The 2026 Comparison`}
            description={metaDescription}
            canonical={canonical}
            ogTitle={`${cfg.competitorName} vs. SplashPad — 2026 comparison`}
            ogDescription={cfg.wedge}
            jsonLd={webPageJsonLd}
            breadcrumb={breadcrumbJsonLd}
            faqJsonLd={faqJsonLd}
        >
            {/* --- HERO / Answer-First ------------------------------------- */}
            <section className="border-b border-[#1a3a5c]/10 bg-white">
                <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-20">
                    <div className="text-xs font-semibold uppercase tracking-widest text-[#00d4ff]">
                        Compare &middot; {cfg.competitorName} alternative &middot; 2026
                    </div>
                    <h1 className="mt-3 font-display text-3xl font-bold leading-tight text-[#0a1628] sm:text-4xl md:text-5xl">
                        {cfg.h1}
                    </h1>
                    <p className="mt-5 max-w-3xl text-base font-semibold text-[#0a1628] sm:text-lg" data-testid="answer-first-paragraph">
                        {cfg.answerFirst}
                    </p>
                    <p className="mt-4 max-w-3xl text-[#1a3a5c]">
                        {cfg.heroSub}
                    </p>

                    {/* High-contrast primary CTA — tested to remain readable
                        in bright outdoor / pool-deck light. */}
                    <div className="mt-6 flex flex-wrap items-center gap-3">
                        <Link to="/signup">
                            <button
                                type="button"
                                data-testid={`vs-${cfg.slug}-primary-cta`}
                                className="inline-flex h-12 items-center gap-2 rounded-xl bg-[#0a1628] px-6 text-base font-bold text-white shadow-lg ring-2 ring-[#0a1628] transition-transform hover:scale-[1.02] hover:bg-[#0a1628]/90 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#00d4ff]"
                            >
                                Start my free 30-day trial <ArrowRight className="h-4 w-4" />
                            </button>
                        </Link>
                        <Link to={`/`}>
                            <button
                                type="button"
                                className="inline-flex h-12 items-center gap-2 rounded-xl border-2 border-[#0a1628]/20 bg-white px-6 text-base font-semibold text-[#0a1628] hover:border-[#0a1628]/40"
                                data-testid={`vs-${cfg.slug}-tour-cta`}
                            >
                                See the product tour
                            </button>
                        </Link>
                    </div>
                </div>
            </section>

            {cfg.pricingSource && <section className="mx-auto max-w-5xl px-4 py-8 sm:px-6" data-testid="swum-published-pricing">
                <h2 className="font-display text-xl font-semibold" data-testid="swum-pricing-heading">Published Swum plan fees</h2>
                <Table data-testid="swum-pricing-table"><TableHeader><TableRow><TableHead>Plan</TableHead><TableHead>Monthly</TableHead><TableHead>Platform</TableHead><TableHead>Processing</TableHead><TableHead>Total transaction fee</TableHead></TableRow></TableHeader><TableBody>
                    {COMPETITOR_FEES.filter((p) => p.id.startsWith('swum-ai-')).map((p) => <TableRow key={p.id} data-testid={`published-${p.id}`}><TableCell>{p.label}</TableCell><TableCell>${p.monthlyFlat}</TableCell><TableCell>{p.platformPct}%</TableCell><TableCell>{p.processingPct}%{p.processingPerTxn > 0 ? ` + $${p.processingPerTxn.toFixed(2)}` : ''}</TableCell><TableCell>{p.platformPct + p.processingPct}%{p.processingPerTxn > 0 ? ` + $${p.processingPerTxn.toFixed(2)}` : ''}</TableCell></TableRow>)}
                </TableBody></Table>
                <p className="mt-3 text-xs text-muted-foreground" data-testid="swum-pricing-source">Checked {cfg.pricingVerifiedAt} against <a href={cfg.pricingSource} target="_blank" rel="noopener noreferrer" className="underline sd-focus" data-testid="swum-pricing-source-link">Swum’s published pricing</a>. USD self-serve plans; Enterprise is separately quoted. Feature scope varies by plan. Estimates exclude taxes, disputes, refunds and international-card extras.</p>
            </section>}

            {/* --- Coach Savings Calculator (competitor-specific pre-select) - */}
            {/*   Phase 0b5 — same interactive calculator that lives on the
                 landing page, but with the dropdown pre-selected to this
                 comparison's competitor so a visitor arriving from a
                 specific search intent sees their platform first.
                 Any config entry in comparisons.js can enable this by
                 setting `showProfitCalculator: true`. */}
            {cfg.showProfitCalculator && (
                <LandingProfitCalculator
                    defaultCompetitorId={SLUG_TO_COMPETITOR_ID[cfg.slug] || 'custom'}
                    anchorId={`savings-calculator-${cfg.slug}`}
                />
            )}

            {/* --- Modern Wedge (H2 contrast) ------------------------------ */}
            <section className="border-b border-[#1a3a5c]/10 bg-[#0a1628] text-white">
                <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-16">
                    <div className="grid gap-6 md:grid-cols-2">
                        <Card className="border-white/10 bg-white/5 p-6 text-white">
                            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-white/60">
                                <Minus className="h-4 w-4" /> {cfg.pricingSource ? cfg.competitorName : 'The old way'}
                            </div>
                            <h2 className="mt-2 font-display text-xl font-bold sm:text-2xl">
                                {cfg.legacyLabel}
                            </h2>
                            <p className="mt-3 text-sm text-white/70">
                                For {cfg.whoIsForThem.toLowerCase()}
                            </p>
                        </Card>
                        <Card className="border-[#00d4ff]/40 bg-[#00d4ff]/10 p-6 text-white ring-2 ring-[#00d4ff]/40">
                            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#00d4ff]">
                                <Zap className="h-4 w-4" /> {cfg.pricingSource ? 'SplashPad' : 'The 2026 way'}
                            </div>
                            <h2 className="mt-2 font-display text-xl font-bold sm:text-2xl">
                                {cfg.modernLabel}
                            </h2>
                            <p className="mt-3 text-sm text-white/80">
                                For {cfg.whoIsForUs.toLowerCase()}
                            </p>
                        </Card>
                    </div>
                </div>
            </section>

            {/* --- Feature-by-feature table -------------------------------- */}
            <section className="bg-[#f4f7fb]">
                <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
                    <h2 className="font-display text-2xl font-bold text-[#0a1628] sm:text-3xl">
                        Feature-by-feature comparison
                    </h2>
                    <p className="mt-2 max-w-3xl text-[#1a3a5c]">
                        Compiled from publicly available information as of 2026. Refer to each vendor for the latest details.
                    </p>
                    <div className="mt-6 overflow-x-auto rounded-xl border border-[#1a3a5c]/10 bg-white shadow-sm">
                        <table className="w-full text-sm" data-testid={`vs-${cfg.slug}-feature-table`}>
                            <thead className="bg-[#f8fafc] text-left text-[#0a1628]">
                                <tr>
                                    <th className="sticky left-0 z-10 bg-[#f8fafc] px-4 py-3 font-semibold">Feature</th>
                                    <th className="px-4 py-3 text-center font-semibold">SplashPad</th>
                                    <th className="px-4 py-3 text-center font-semibold">{cfg.competitorShort}</th>
                                    <th className="px-4 py-3 font-semibold">Notes</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[#1a3a5c]/5">
                                {cfg.features.map((row) => (
                                    <tr key={row.feature} className="align-top">
                                        <td className="sticky left-0 z-10 bg-white px-4 py-3 font-medium text-[#0a1628]">{row.feature}</td>
                                        <td className="px-4 py-3 text-center"><Cell value={row.splashpad} /></td>
                                        <td className="px-4 py-3 text-center"><Cell value={row.competitor} /></td>
                                        <td className="px-4 py-3 text-[#1a3a5c]">{row.note}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <p className="mt-3 text-xs text-[#1a3a5c]/70">
                        <Check className="inline h-3.5 w-3.5 text-[#10b981]" /> Yes &middot; <Minus className="inline h-3.5 w-3.5 text-[#94a3b8]" /> Partial &middot; <X className="inline h-3.5 w-3.5 text-[#ef4444]" /> Limited or requires add-on.
                    </p>
                </div>
            </section>

            {/* --- Pricing wedge ------------------------------------------- */}
            <section className="border-t border-[#1a3a5c]/10 bg-white">
                <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-16">
                    <h2 className="font-display text-2xl font-bold text-[#0a1628] sm:text-3xl">
                        Pricing built for independent coaches
                    </h2>
                    <div className="mt-6 grid gap-4 md:grid-cols-3">
                        <Card className="border-[#19c37d]/40 bg-[#19c37d]/5 p-5">
                            <Badge className="bg-[#19c37d] text-white">Free for SSU members</Badge>
                            <h3 className="mt-3 font-display text-xl font-bold text-[#0a1628]">Starter</h3>
                            <p className="mt-1 text-3xl font-bold text-[#0a1628]">$39.99<span className="text-sm text-[#1a3a5c]/70">/mo</span></p>
                            <p className="mt-2 text-sm text-[#1a3a5c]">Everything you need to launch. Up to 40 active students. <strong className="text-[#19c37d]">Free for verified Superhero Swim University Skool community members.</strong></p>
                        </Card>
                        <Card className="border-[#ffd700] bg-[#ffd700]/10 p-5">
                            <Badge className="bg-[#ffd700] text-[#0a1628]">Most popular</Badge>
                            <h3 className="mt-3 font-display text-xl font-bold text-[#0a1628]">Pro</h3>
                            <p className="mt-1 text-3xl font-bold text-[#0a1628]">$79.99<span className="text-sm text-[#1a3a5c]/70">/mo</span></p>
                            <p className="mt-2 text-sm text-[#1a3a5c]">Unlimited students, drag-and-drop landing page, multi-coach staff portal, and broadcast tools.</p>
                        </Card>
                        <Card className="border-[#1a3a5c]/10 bg-[#f4f7fb] p-5">
                            <Badge className="bg-[#0a1628] text-white">Try free</Badge>
                            <h3 className="mt-3 font-display text-xl font-bold text-[#0a1628]">30-day trial</h3>
                            <p className="mt-1 text-3xl font-bold text-[#0a1628]">$0</p>
                            <p className="mt-2 text-sm text-[#1a3a5c]">Full access to SplashPad for 30 days. Cancel anytime.</p>
                        </Card>
                    </div>
                    <p className="mt-4 text-sm text-[#1a3a5c]">
                        No per-family fees. No per-enrollment fees. No add-on module fees. Stripe processing rates apply to card payments.
                    </p>
                </div>
            </section>

            {/* --- Migration + Speed FAQs (Answer-First for LLM extract) --- */}
            <section className="bg-[#f4f7fb]">
                <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 sm:py-16">
                    <h2 className="font-display text-2xl font-bold text-[#0a1628] sm:text-3xl">
                        Frequently asked questions
                    </h2>
                    <div className="mt-6 space-y-6">
                        <div>
                            <h3 className="flex items-center gap-2 font-semibold text-[#0a1628]">
                                <Zap className="h-4 w-4 text-[#00d4ff]" />
                                {cfg.fasterQuestion}
                            </h3>
                            <p className="mt-2 text-[#1a3a5c]">{cfg.fasterAnswer}</p>
                        </div>
                        <div>
                            <h3 className="flex items-center gap-2 font-semibold text-[#0a1628]">
                                <ChevronRight className="h-4 w-4 text-[#00d4ff]" />
                                {cfg.migrationQuestion}
                            </h3>
                            <p className="mt-2 text-[#1a3a5c]">{cfg.migrationAnswer}</p>
                        </div>
                        <div>
                            <h3 className="flex items-center gap-2 font-semibold text-[#0a1628]">
                                <Waves className="h-4 w-4 text-[#00d4ff]" />
                                Who is SplashPad better for than {cfg.competitorName}?
                            </h3>
                            <p className="mt-2 text-[#1a3a5c]">
                                {cfg.whoIsForUs} {cfg.competitorName} remains a good fit for {cfg.whoIsForThem.toLowerCase()}.
                            </p>
                        </div>
                        <div>
                            <h3 className="flex items-center gap-2 font-semibold text-[#0a1628]">
                                <Shield className="h-4 w-4 text-[#00d4ff]" />
                                Is SplashPad secure enough to switch to?
                            </h3>
                            <p className="mt-2 text-[#1a3a5c]">
                                Yes. SplashPad ships two-factor authentication, HaveIBeenPwned breach-checked passwords, IP rate limits, account lockouts, hardened HTTP headers, and a full audit log on every money move. Card data never touches SplashPad servers &mdash; it flows directly from the parent&rsquo;s browser to Stripe. <Link to="/security" className="text-[#00d4ff] underline">Read the full security page</Link>.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* --- Related comparisons ------------------------------------- */}
            <section className="border-t border-[#1a3a5c]/10 bg-white">
                <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6">
                    <div className="rounded-xl border border-[#1a3a5c]/10 bg-[#f4f7fb] p-6">
                        <div className="flex items-center gap-2 text-[#00d4ff]">
                            <Sparkles className="h-4 w-4" />
                            <h3 className="font-semibold text-[#0a1628]">Compare SplashPad to other platforms</h3>
                        </div>
                        <ul className="mt-3 grid gap-2 text-sm text-[#1a3a5c] sm:grid-cols-2">
                            <li><Link to="/vs/jackrabbit-swim" className="underline hover:text-[#00d4ff]">SplashPad vs. Jackrabbit Swim</Link></li>
                            <li><Link to="/vs/iclasspro" className="underline hover:text-[#00d4ff]">SplashPad vs. iClassPro</Link></li>
                            <li><Link to="/vs/mindbody" className="underline hover:text-[#00d4ff]">SplashPad vs. Mindbody</Link></li>
                            <li><Link to="/vs/swimtopia" className="underline hover:text-[#00d4ff]">SplashPad vs. SwimTopia</Link></li>
                            <li><Link to="/vs/teamunify" className="underline hover:text-[#00d4ff]">SplashPad vs. TeamUnify</Link></li>
                            <li><Link to="/best-swim-school-software" className="underline hover:text-[#00d4ff]">The best swim school software (2026)</Link></li>
                        </ul>
                    </div>

                    {/* SoftwareApplication schema for this specific
                        comparison, injected as a plain JSON-LD script
                        wrapped in a Helmet-friendly hidden node. */}
                    <script
                        type="application/ld+json"
                        dangerouslySetInnerHTML={{ __html: JSON.stringify(softwareJsonLd) }}
                    />
                </div>
            </section>
        </SeoPageShell>
    );
};

export default ComparisonPage;
